import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercent,
  getLocaleNumberSymbol,
  NumberSymbol,
} from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Input, LOCALE_ID, SimpleChanges } from '@angular/core';
import { ComparatorOptions, isIterable, isString, orderBy, OrderByOptions } from '@trilean/n7';
import { ThemeColors } from '../../theme/theme-colors';

export type DataDisplayAlignment = 'left' | 'center' | 'right' | 'justified';

export function getDefaultAlignment(type: DataDisplayType): DataDisplayAlignment | undefined {
  switch (type) {
    case 'text':
      return 'left';
    case 'number':
    case 'integer':
    case 'decimal-1':
    case 'decimal-2':
    case 'decimal-3':
    case 'decimal-4':
    case 'decimal-5':
    case 'decimal-6':
    case 'decimal-7':
    case 'decimal-8':
      return 'center';
    case 'fixed-1':
    case 'fixed-2':
    case 'fixed-3':
    case 'fixed-4':
    case 'fixed-5':
    case 'fixed-6':
    case 'fixed-7':
    case 'fixed-8':
    case 'percent':
    case 'currency':
      return 'right';
    case 'sparkline-line':
    case 'sparkline-bar':
    case 'sparkline-column':
      return 'center';
    case 'date-short':
    case 'time':
    case 'time-short':
    case 'datetime':
    case 'datetime-short':
    case 'date':
    default:
      return undefined;
  }
}

type DataDisplayListSeparator = string;

export type DataDisplayListConfig = {
  prefix?: string;
  suffix?: string;
  separator?: string;
  order?: OrderByOptions | ComparatorOptions['order'];
};

function getDataDisplayListConfig(value: DataDisplayListSeparator | DataDisplayListConfig): DataDisplayListConfig {
  if (isString(value)) {
    return { separator: value };
  } else {
    return value;
  }
}

type DataDisplayTypeHidden = 'hidden';

interface DataDisplayConfigHidden {
  type: DataDisplayTypeHidden;
  align?: DataDisplayAlignment;
}

type DataDisplayTypeSimple =
  | 'text'
  | 'decimal-1'
  | 'decimal-2'
  | 'decimal-3'
  | 'decimal-4'
  | 'decimal-5'
  | 'decimal-6'
  | 'decimal-7'
  | 'decimal-8'
  | 'fixed-1'
  | 'fixed-2'
  | 'fixed-3'
  | 'fixed-4'
  | 'fixed-5'
  | 'fixed-6'
  | 'fixed-7'
  | 'fixed-8'
  | 'date-short'
  | 'time'
  | 'time-short'
  | 'datetime'
  | 'datetime-short';

interface DataDisplayConfigSimple {
  type: DataDisplayTypeSimple;
  align?: DataDisplayAlignment;
  prefix?: string;
  suffix?: string;
  list?: DataDisplayListConfig;
}

type DataDisplayTypeFormattable = 'number' | 'integer' | 'percent' | 'currency' | 'date';

interface DataDisplayConfigFormattable {
  type: DataDisplayTypeFormattable;
  format?: string;
  align?: DataDisplayAlignment;
  prefix?: string;
  suffix?: string;
  list?: DataDisplayListConfig;
}

type DataDisplayTypeSparkline = 'sparkline-line' | 'sparkline-bar' | 'sparkline-column';

interface DataDisplayConfigSparkline {
  type: DataDisplayTypeSparkline;
  align?: DataDisplayAlignment;
  min?: number;
  max?: number;
  palette?: string[];
  markers?: number | boolean;
}

export type DataDisplayType =
  | DataDisplayTypeHidden
  | DataDisplayTypeSimple
  | DataDisplayTypeFormattable
  | DataDisplayTypeSparkline;

export type DataDisplayConfig =
  | DataDisplayConfigHidden
  | DataDisplayConfigSimple
  | DataDisplayConfigFormattable
  | DataDisplayConfigSparkline;

@Component({
  selector: 'tr3-data, td[display]',
  templateUrl: './data-display.component.html',
  styleUrls: ['./data-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataDisplayComponent {
  @Input() type: DataDisplayType = 'text';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() value: any;
  @Input() format?: string;
  @Input() align?: DataDisplayAlignment;
  @Input() prefix?: string;
  @Input() suffix?: string;
  @Input() min?: number;
  @Input() max?: number;
  @Input() palette?: string[];
  @Input() markers?: number | boolean;
  @Input() list?: DataDisplayListConfig;

  @Input()
  get display(): DataDisplayConfig | string {
    return <DataDisplayConfig>{
      type: this.type,
      format: this.format,
      align: this.align,
      prefix: this.prefix,
      suffix: this.suffix,
      min: this.min,
      max: this.max,
      palette: this.palette,
      markers: this.markers,
      list: this.list,
    };
  }
  set display(value: DataDisplayConfig | string) {
    value ??= 'text';
    value = isString(value) ? <DataDisplayConfig>{ type: value } : value;
    switch (value?.type) {
      case 'text':
      case 'decimal-1':
      case 'decimal-2':
      case 'decimal-3':
      case 'decimal-4':
      case 'decimal-5':
      case 'decimal-6':
      case 'decimal-7':
      case 'decimal-8':
      case 'fixed-1':
      case 'fixed-2':
      case 'fixed-3':
      case 'fixed-4':
      case 'fixed-5':
      case 'fixed-6':
      case 'fixed-7':
      case 'fixed-8':
      case 'date-short':
      case 'time':
      case 'time-short':
      case 'datetime':
      case 'datetime-short':
        this.type = value.type;
        this.align = value.align;
        this.prefix = value.prefix;
        this.suffix = value.suffix;
        this.list = getDataDisplayListConfig(value.list ?? this.defaultListSeparator);
        break;
      case 'number':
      case 'integer':
      case 'percent':
      case 'currency':
      case 'date':
        this.type = value.type;
        this.align = value.align;
        this.prefix = value.prefix;
        this.suffix = value.suffix;
        this.format = value.format;
        this.list = getDataDisplayListConfig(value.list ?? this.defaultListSeparator);
        break;
      case 'sparkline-line':
      case 'sparkline-bar':
      case 'sparkline-column':
        this.type = value.type;
        this.align = value.align;
        this.min = value.min;
        this.max = value.max;
        this.palette = value.palette;
        this.markers = value.markers;
        break;
      case 'hidden':
      default:
        this.type = value.type ?? 'text';
        break;
    }
    this.align = value.align ?? getDefaultAlignment(this.type);
  }

  displayValue = '';
  defaultListSeparator: string;

  constructor(@Inject(LOCALE_ID) private _localeId: string) {
    this.defaultListSeparator = getLocaleNumberSymbol(this._localeId, NumberSymbol.List);
    this.list = getDataDisplayListConfig(this.defaultListSeparator);
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.displayValue = this.formatValue(this.value);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private formatValue(value: any): string {
    const str = isIterable(value) ? this.formatIterableValue(value) : this.formatSimpleValue(value);
    return `${this.prefix ?? ''}${str}${this.suffix ?? ''}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private formatIterableValue(values: Iterable<any>) {
    this.list = getDataDisplayListConfig(this.list ?? this.defaultListSeparator);
    if (!!this.list?.order) {
      const order = isString(this.list.order) ? <OrderByOptions>{ order: this.list.order } : this.list.order;
      values = orderBy(values, order);
    }
    const str = [...values].reduce(
      (acc, val, i) =>
        i > 0 ? `${acc}${this.list?.separator}${this.formatSimpleValue(val)}` : `${this.formatSimpleValue(val)}`,
      '',
    );
    return `${this.list?.prefix ?? ''}${str}${this.list?.suffix ?? ''}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private formatSimpleValue(value: any) {
    switch (this.type) {
      case 'text':
        return `${value}`;
      case 'number':
        return formatNumber(value, this._localeId, this.format);
      case 'integer':
        return formatNumber(value, this._localeId, '1.0-0');
      case 'decimal-1':
        return formatNumber(value, this._localeId, '1.0-1');
      case 'decimal-2':
        return formatNumber(value, this._localeId, '1.0-2');
      case 'decimal-3':
        return formatNumber(value, this._localeId, '1.0-3');
      case 'decimal-4':
        return formatNumber(value, this._localeId, '1.0-4');
      case 'decimal-5':
        return formatNumber(value, this._localeId, '1.0-5');
      case 'decimal-6':
        return formatNumber(value, this._localeId, '1.0-6');
      case 'decimal-7':
        return formatNumber(value, this._localeId, '1.0-7');
      case 'decimal-8':
        return formatNumber(value, this._localeId, '1.0-8');
      case 'fixed-1':
        return formatNumber(value, this._localeId, '1.1-1');
      case 'fixed-2':
        return formatNumber(value, this._localeId, '1.2-2');
      case 'fixed-3':
        return formatNumber(value, this._localeId, '1.3-3');
      case 'fixed-4':
        return formatNumber(value, this._localeId, '1.4-4');
      case 'fixed-5':
        return formatNumber(value, this._localeId, '1.5-5');
      case 'fixed-6':
        return formatNumber(value, this._localeId, '1.6-6');
      case 'fixed-7':
        return formatNumber(value, this._localeId, '1.7-7');
      case 'fixed-8':
        return formatNumber(value, this._localeId, '1.8-8');
      case 'percent':
        return formatPercent(value, this._localeId, this.format);
      case 'currency':
        return formatCurrency(value, this._localeId, this.format ?? '');
      case 'date':
        return formatDate(value, this.format ?? 'mediumDate', this._localeId);
      case 'date-short':
        return formatDate(value, this.format ?? 'shortDate', this._localeId);
      case 'time':
        return formatDate(value, this.format ?? 'mediumTime', this._localeId);
      case 'time-short':
        return formatDate(value, this.format ?? 'shortTime', this._localeId);
      case 'datetime':
        return formatDate(value, this.format ?? 'medium', this._localeId);
      case 'datetime-short':
        return formatDate(value, this.format ?? 'short', this._localeId);
      case 'hidden':
      default:
        return '';
    }
  }

  getPalette(): string[] {
    return this.palette ?? [ThemeColors.getAccent()];
  }
}
